// Host-derived tenant resolution helpers.
//
// The same SPA build serves every alias for the OptimalHR domain
// (`app.optimal-hr.com`, `<slug>.optimal-hr.com`, etc.). At boot the
// app inspects `window.location.hostname` so a user landing directly
// on `<slug>.optimal-hr.com` skips the workspace-picker step. From the
// picker host (`app.<env-base>`) the welcome form swaps a same-host
// navigation for a cross-subdomain redirect, putting the user on
// their tenant URL before Cognito sign-in.
//
// These helpers are intentionally pure: the boot path and the welcome
// form pass `window.location.hostname` in explicitly so the same code
// is unit-testable against any topology (dev, prod, localhost,
// custom-domain tenants in the future).

// Reserved leftmost-host labels. Must mirror the SaaS-Console-side
// reserved-slug enforcement (`HANDOFF_NEXT_PHASE.md` PR-S5) so a host
// like `app.dev.optimal-hr.com` is never mistaken for a tenant called
// "app". Order matches the doc; flatten conventions, env aliases, and
// the placeholder dist subdomains in one set.
const RESERVED_HOST_LABELS = new Set([
  'www',
  'app',
  'admin',
  'docs',
  'help',
  'support',
  'api',
  'status',
  'blog',
  'mail',
  'login',
  'signin',
  'signup',
  'auth',
  'cdn',
  'static',
  'assets',
  'marketing',
  'careers',
  'jobs',
  'team',
  'about',
  // Env-band labels (the bare apex of a non-prod environment).
  'dev',
  'staging',
  'prod',
  'test',
]);

// Mirrors the WORKSPACE_PATTERN in WelcomePage.vue: either a 6-digit
// numeric org id, or a kebab-case slug starting with a letter and
// ending with a letter / digit.
const SLUG_PATTERN = /^(\d{6}|[a-z][a-z0-9-]{1,30}[a-z0-9])$/;

/**
 * If `hostname` resolves to a tenant subdomain (e.g. `dcc.optimal-hr.com`,
 * `acme.dev.optimal-hr.com`), return that leftmost label; otherwise
 * return `null`. Reserved labels (`app`, `docs`, `dev`, …), apex hosts
 * without a subdomain (`optimal-hr.com`), and non-domain hosts
 * (`localhost`, raw IPs) all yield `null`.
 */
export function deriveTenantSlugFromHost(hostname: string): string | null {
  if (!hostname.includes('.')) return null; // localhost, single-label, etc.
  if (/^[\d.]+$/.test(hostname)) return null; // raw IPv4
  const parts = hostname.split('.');
  // Apex domains have exactly two labels (eg. `optimal-hr.com`). They
  // have no subdomain to derive a tenant from; the leftmost label is
  // part of the brand, not a slug.
  if (parts.length < 3) return null;
  const leftmost = parts[0];
  if (!leftmost) return null;
  if (RESERVED_HOST_LABELS.has(leftmost)) return null;
  if (!SLUG_PATTERN.test(leftmost)) return null;
  return leftmost;
}

/**
 * Construct the tenant-scoped URL a picker should redirect to after
 * the user selects a workspace slug. Strips the leftmost host label
 * when it's either the picker prefix (`app.`) OR an existing tenant
 * slug — so a user on `dcc.optimal-hr.com` switching to `optimalp`
 * lands on `https://optimalp.optimal-hr.com/...`, not the nested
 * `https://optimalp.dcc.optimal-hr.com/...`. Env-band labels
 * (`dev`, `staging`, `prod`, `test`) are kept intact since they're
 * part of the base, not a tenant.
 *
 * Returns `null` when cross-host redirect isn't possible — `localhost`,
 * raw IPs, single-label hosts — so the caller can fall back to a
 * same-host router push (typical in local dev).
 */
export function getTenantSignInUrl(
  slug: string,
  hostname: string,
  options: { redirect?: string } = {},
): string | null {
  if (!hostname.includes('.')) return null;
  if (/^[\d.]+$/.test(hostname)) return null;

  let base = hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const leftmost = parts[0]!;
    const isPickerPrefix = leftmost === 'app';
    const isTenantPrefix = !RESERVED_HOST_LABELS.has(leftmost) && SLUG_PATTERN.test(leftmost);
    if (isPickerPrefix || isTenantPrefix) {
      base = parts.slice(1).join('.');
    }
  }

  const path = '/sign-in';
  const query = options.redirect ? `?redirect=${encodeURIComponent(options.redirect)}` : '';
  return `https://${slug}.${base}${path}${query}`;
}
